import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { PulseLoader } from "react-spinners";

const API_URL = process.env.REACT_APP_API_URL;

function parseApiResponse(raw) {
  let d = raw;
  if (typeof d === "string") {
    try {
      d = JSON.parse(d);
    } catch {
      return raw;
    }
  }
  if (d?.body != null) {
    if (typeof d.body === "string") {
      try {
        return JSON.parse(d.body);
      } catch {
        return d.body;
      }
    }
    return d.body;
  }
  return d;
}

function parseJsonIfString(value) {
  if (typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}

function normalizeApiPayload(raw) {
  let obj = raw;
  if (!obj || typeof obj !== "object") return raw;
  obj = { ...obj };
  if (typeof obj.data === "string") {
    obj.data = parseJsonIfString(obj.data);
  }
  if (
    obj.data &&
    typeof obj.data === "object" &&
    typeof obj.data.data === "string"
  ) {
    obj.data = { ...obj.data, ...parseJsonIfString(obj.data.data) };
  }
  return obj;
}

function isApiSuccess(responseData) {
  if (!responseData || typeof responseData !== "object") return false;
  const s = responseData.success;
  return s === true || s === "true" || s === 1 || s === "1";
}

function pickGoFromResponse(responseData) {
  let dataBlock = responseData?.data != null ? responseData.data : responseData;
  if (dataBlock == null) return null;
  if (typeof dataBlock === "string") {
    dataBlock = parseJsonIfString(dataBlock);
  }
  if (!dataBlock || typeof dataBlock !== "object") return null;

  if (dataBlock.goodsOutwards && typeof dataBlock.goodsOutwards === "object") {
    return dataBlock.goodsOutwards;
  }
  if (dataBlock.go && typeof dataBlock.go === "object") {
    return dataBlock.go;
  }
  if (
    !Array.isArray(dataBlock) &&
    (dataBlock._id != null ||
      dataBlock.id != null ||
      dataBlock.goNumber != null)
  ) {
    return dataBlock;
  }
  return null;
}

function displayValue(value) {
  if (value == null || value === "") return "-";
  return String(value);
}

function displayQty(value) {
  if (value == null || value === "") return "-";
  return value;
}

function displayUser(ref) {
  if (!ref) return "-";
  if (typeof ref === "string") return ref.trim() || "-";
  return ref.name || ref.userName || ref.email || "-";
}

function formatDateParts(date) {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(date);
  const get = (type) => parts.find((p) => p.type === type)?.value || "";
  return `${get("day")}/${get("month")}/${get("year")}`;
}

function formatDate(value) {
  if (!value) return "-";
  const str = String(value).trim();
  if (/^\d{2}-\d{2}-\d{4}$/.test(str)) return str.replace(/-/g, "/");
  const dateTimeMatch = str.match(/^(\d{2})-(\d{2})-(\d{4})[ T]/);
  if (dateTimeMatch) {
    return `${dateTimeMatch[1]}/${dateTimeMatch[2]}/${dateTimeMatch[3]}`;
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return str || "-";
  return formatDateParts(d);
}

function formatDateTime(value) {
  if (!value) return "-";
  const str = String(value).trim();
  const dateTimeMatch = str.match(
    /^(\d{2})-(\d{2})-(\d{4})[ T](\d{2}):(\d{2})/,
  );
  if (dateTimeMatch) {
    const [, dd, mm, yyyy, hh, min] = dateTimeMatch;
    const hour = parseInt(hh, 10);
    const period = hour >= 12 ? "PM" : "AM";
    const h12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${dd}/${mm}/${yyyy}, ${String(h12).padStart(2, "0")}:${min} ${period}`;
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return str || "-";
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  }).formatToParts(d);
  const get = (type) => parts.find((p) => p.type === type)?.value || "";
  const dayPeriod = get("dayPeriod").toUpperCase();
  return `${get("day")}/${get("month")}/${get("year")}, ${get("hour")}:${get("minute")} ${dayPeriod}`;
}

function capitalizeStatus(value) {
  if (!value) return "-";
  const s = String(value);
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}

function renderStatus(status) {
  const normalized = String(status || "").toLowerCase();
  const label = capitalizeStatus(status);

  if (normalized === "inactive") {
    return (
      <span className="fwt_600 f_red" style={{ fontSize: "90%", fontWeight: 550 }}>
        {label}
      </span>
    );
  }
  if (normalized === "active") {
    return (
      <span
        className="badge badge-success"
        style={{ fontSize: "90%", fontWeight: 550 }}
      >
        {label}
      </span>
    );
  }
  return (
    <span
      className={`badge ${normalized === "draft" ? "badge-secondary" : "badge-info"}`}
      style={{ fontSize: "90%", fontWeight: 550 }}
    >
      {label}
    </span>
  );
}

const ViewField = ({ label, children }) => (
  <div className="form-group row align-items-center">
    <label className="col-12 col-md-4 col-form-label">{label}</label>
    <div className="col-12 col-md-8">
      <div className="txtdsply">{children}</div>
    </div>
  </div>
);

const SectionTitle = ({ icon, title }) => (
  <div className="row mb-2 mt-3">
    <div className="col-12">
      <h5 className="text-primary border-bottom pb-2 mb-3">
        <i className={`fa-solid ${icon} mr-2`} />
        {title}
      </h5>
    </div>
  </div>
);

function GoodsOutwardsView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [go, setGo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchGo = useCallback(async () => {
    if (!id) {
      toast.error("Valid id is required", { autoClose: 2500 });
      navigate("/goodsOutwards");
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(API_URL, {
        action: "get-goods-outwards-by-id",
        id,
      });
      const responseData = normalizeApiPayload(parseApiResponse(response.data));

      if (!isApiSuccess(responseData)) {
        toast.error(
          responseData?.message || "Failed to fetch goods outwards",
          { autoClose: 3000 },
        );
        navigate("/goodsOutwards");
        return;
      }

      const record = pickGoFromResponse(responseData);
      if (!record) {
        toast.error("Goods outwards not found", { autoClose: 3000 });
        navigate("/goodsOutwards");
        return;
      }
      setGo(record);
    } catch (error) {
      console.error("Fetch goods outwards by id error:", error);
      const errData = error.response?.data
        ? parseApiResponse(error.response.data)
        : null;
      const status = error.response?.status;
      const message =
        (typeof errData === "object" && errData?.message) ||
        (status === 404
          ? "Goods outwards not found"
          : status === 400
            ? "Valid id is required"
            : "Failed to fetch goods outwards");
      toast.error(message, { autoClose: 3000 });
      navigate("/goodsOutwards");
    } finally {
      setIsLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchGo();
  }, [fetchGo]);

  const materials = useMemo(
    () => (Array.isArray(go?.materials) ? go.materials : []),
    [go],
  );

  const header = (
    <div className="content-header">
      <div className="container-fluid">
        <div className="row mb-2 align-items-center">
          <div className="col-6 col-sm-6">
            <h1 className="m-0 txt_lft">View Goods Outwards</h1>
          </div>
          <div className="col-6 col-sm-6">
            <ol className="breadcrumb justify-content-end mb-0">
              <li className="breadcrumb-item">
                <Link to="/goodsOutwards">
                  <i className="fa-solid fa-arrow-left-long" /> Back
                </Link>
              </li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div>
        {header}
        <div className="content">
          <div className="container-fluid">
            <div className="row">
              <div className="frmwdth_cntr">
                <div className="col-lg-12">
                  <div className="card card-primary card-outline">
                    <div className="card-body text-center py-5">
                      <PulseLoader color="#1199B2" size={10} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!go) {
    return (
      <div>
        {header}
        <div className="content">
          <div className="container-fluid">
            <div className="row">
              <div className="frmwdth_cntr">
                <div className="col-lg-12">
                  <div className="card card-primary card-outline">
                    <div className="card-body text-center py-5">
                      <p>Goods outwards not found</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {header}
      <div className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="frmwdth_cntr">
              <div className="col-lg-12">
                <div className="card card-primary card-outline">
                  <div className="card-body">
                    <SectionTitle icon="fa-file-lines" title="GO Details" />

                    <ViewField label="GO Number">
                      {displayValue(go.goNumber)}
                    </ViewField>
                    <ViewField label="GO Date">{formatDate(go.goDate)}</ViewField>
                    <ViewField label="GO Summary">
                      {displayValue(go.goSummary)}
                    </ViewField>
                    <ViewField label="SO Number">
                      {displayValue(go.soNumber)}
                    </ViewField>
                    <ViewField label="SO Date">{formatDate(go.soDate)}</ViewField>
                    <ViewField label="Username">
                      {displayValue(go.username)}
                    </ViewField>
                    <ViewField label="Client Reference">
                      {displayValue(go.clientReference)}
                    </ViewField>
                    <ViewField label="Department">
                      {displayValue(go.department)}
                    </ViewField>
                    <ViewField label="Status">{renderStatus(go.status)}</ViewField>

                    <SectionTitle icon="fa-boxes-stacked" title="Materials" />

                    {materials.length === 0 ? (
                      <div className="txtdsply text-muted mb-3">No materials</div>
                    ) : (
                      <div className="mb-3" style={{ overflowX: "hidden" }}>
                        <table
                          className="table table-bordered table-sm mb-0"
                          style={{
                            width: "100%",
                            tableLayout: "fixed",
                            fontSize: "11px",
                          }}
                        >
                          <thead>
                            <tr
                              style={{ backgroundColor: "#003366", color: "#fff" }}
                            >
                              <th style={{ width: "4%", padding: "4px 2px" }}>
                                S.No
                              </th>
                              <th style={{ width: "12%", padding: "4px 2px" }}>
                                Material Code
                              </th>
                              <th style={{ width: "22%", padding: "4px 2px" }}>
                                Material Description
                              </th>
                              <th style={{ width: "8%", padding: "4px 2px" }}>
                                HSN
                              </th>
                              <th style={{ width: "6%", padding: "4px 2px" }}>
                                UOM
                              </th>
                              <th style={{ width: "12%", padding: "4px 2px" }}>
                                Whose Stock
                              </th>
                              <th style={{ width: "10%", padding: "4px 2px" }}>
                                Available Stock
                              </th>
                              <th style={{ width: "10%", padding: "4px 2px" }}>
                                Goods Outwards
                              </th>
                              <th style={{ width: "12%", padding: "4px 2px" }}>
                                Updated Available
                              </th>
                              <th style={{ width: "8%", padding: "4px 2px" }}>
                                Inventory
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {materials.map((row, index) => (
                              <tr key={row._id || row.id || index}>
                                <td style={{ padding: "4px 2px", whiteSpace: "nowrap" }}>
                                  {index + 1}
                                </td>
                                <td
                                  style={{
                                    padding: "4px 2px",
                                    whiteSpace: "normal",
                                    wordBreak: "break-word",
                                  }}
                                >
                                  {displayValue(row.materialCode)}
                                </td>
                                <td
                                  style={{
                                    padding: "4px 2px",
                                    whiteSpace: "normal",
                                    wordBreak: "break-word",
                                  }}
                                >
                                  {displayValue(row.materialDescription)}
                                </td>
                                <td
                                  style={{
                                    padding: "4px 2px",
                                    whiteSpace: "normal",
                                    wordBreak: "break-word",
                                  }}
                                >
                                  {displayValue(row.hsnCode)}
                                </td>
                                <td style={{ padding: "4px 2px", whiteSpace: "nowrap" }}>
                                  {displayValue(row.uom)}
                                </td>
                                <td style={{ padding: "4px 2px", whiteSpace: "nowrap" }}>
                                  {displayValue(row.whoseStock)}
                                </td>
                                <td style={{ padding: "4px 2px", whiteSpace: "nowrap" }}>
                                  {displayQty(row.availableStock)}
                                </td>
                                <td style={{ padding: "4px 2px", whiteSpace: "nowrap" }}>
                                  {displayQty(row.goodsOutwards)}
                                </td>
                                <td style={{ padding: "4px 2px", whiteSpace: "nowrap" }}>
                                  {displayQty(row.updatedAvailableStock)}
                                </td>
                                <td style={{ padding: "4px 2px", whiteSpace: "nowrap" }}>
                                  {row.inventoryUpdated === true
                                    ? "Updated"
                                    : row.inventoryUpdated === false
                                      ? "Pending"
                                      : "-"}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    <SectionTitle icon="fa-clock" title="Audit" />

                    <ViewField label="Created By">
                      {displayUser(go.createdBy)}
                    </ViewField>
                    <ViewField label="Updated By">
                      {displayUser(go.updatedBy)}
                    </ViewField>
                    <ViewField label="Created At">
                      {formatDateTime(go.createdAt)}
                    </ViewField>
                    <ViewField label="Updated At">
                      {formatDateTime(go.updatedAt)}
                    </ViewField>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default GoodsOutwardsView;
